// Ownership and ordering for callback-based clients. Activation belongs to the
// committed view; abandoned renders cannot invalidate the still-visible view.
export function createLatestRequests(readIdentity: () => unknown = () => undefined) {
    let active=false, key:string, identity:unknown, epoch=0
    const lanes=new Map<string,{stop:()=>void}>(), writes=new Map<unknown,Map<string,Promise<unknown>>>()
    const clear=()=>{lanes.forEach(marker=>marker.stop());lanes.clear()}
    return {
        get generation(){return epoch},
        activeFor:(viewKey:string,generation:number)=>active && key===viewKey && epoch===generation && identity===readIdentity(),
        enter(nextKey:string) { clear();active=true;key=nextKey;identity=readIdentity();epoch++ },
        leave() { active=false;epoch++;clear() },
        begin(lane:string, viewKey:string) {
            const selectedEpoch=epoch, owner=readIdentity(), cleanup=new Set<()=>void>()
            let closed=false
            const marker={stop:()=>{if(closed)return;closed=true;cleanup.forEach(fn=>{try{fn()}catch{console.debug('Request cleanup failed')}});cleanup.clear()}}
            const admitted=active && key===viewKey && identity===owner
            if(admitted){lanes.get(lane)?.stop();lanes.set(lane,marker)}
            else marker.stop()
            return {owner,
                sameOwner:()=>owner===readIdentity(),
                current:()=>!closed && admitted && active && epoch===selectedEpoch && key===viewKey && lanes.get(lane)===marker && owner===readIdentity(),
                own:(fn:()=>void)=>{if(closed)fn();else cleanup.add(fn)},
                finish:()=>{marker.stop();if(lanes.get(lane)===marker)lanes.delete(lane)},
            }
        },
        enqueue<T>(lane:string, owner:unknown, work:()=>Promise<T>) {
            let ownerWrites=writes.get(owner)
            if(!ownerWrites){ownerWrites=new Map();writes.set(owner,ownerWrites)}
            const next=(ownerWrites.get(lane)||Promise.resolve()).then(()=>owner===readIdentity()?work():undefined)
            const settled=next.then(()=>{},()=>{})
            ownerWrites.set(lane,settled)
            return next.finally(()=>{
                if(ownerWrites.get(lane)===settled)ownerWrites.delete(lane)
                if(!ownerWrites.size && writes.get(owner)===ownerWrites)writes.delete(owner)
            })
        },
        afterWrites<T>(lane:string, owner:unknown, work:()=>Promise<T>) {
            return (writes.get(owner)?.get(lane)||Promise.resolve()).then(()=>owner===readIdentity()?work():undefined)
        },
    }
}
